import { Dispatch, SetStateAction, useEffect } from 'react';
import { useUnitAssignment } from '../unit-assignment/unit-assignment-context';
import { CreateUnitEmergencyRequest } from '@mark43/cad-api';
import { useEnabledFeatures } from '../common/hooks/enabled-features/use-enabled-features';
import { useFullscreenModalControls } from '../modals/context/modal-context-provider';
import { INTERVAL_DELAY_VALUE, MILLIS_OF_SECONDS } from './unit-emergencies-constants';
import { useUnitEmergency } from './use-unit-emergency';
import { useBootstrapState } from '../bootstrap/bootstrap-provider';

export function useUnitEmergencyUtils(): {
    toggleEmergencyTimer: () => void;
    timer: boolean;
    secondsRemaining: number;
    setCancelEmergencyModalOpen: Dispatch<SetStateAction<boolean>>;
    handleEmergencyTimer: () => void;
} {
    const {
        secondsRemaining,
        setSecondsRemaining,
        cancelEmergencyModalOpen,
        setCancelEmergencyModalOpen,
        activeUnitEmergency,
        timer,
        setTimer,
        endTime,
        setEndTime,
    } = useUnitEmergency();
    const enabledFeatures = useEnabledFeatures();
    const { unitEmergencyActivationCountdownSeconds } = useBootstrapState();
    const { unitEmergencyActivationCountdownEnabled } = enabledFeatures;
    const unitAssignment = useUnitAssignment();
    const { createUnitEmergency } = useUnitEmergency();
    const modalControls = useFullscreenModalControls();

    useEffect(() => {
        // Prevents the useEffect from firing twice due to setState dependencies
        if (!endTime || !timer) return;

        const updateCountDown = () => {
            const timeNow = new Date().getTime();
            const endTimeToDate = endTime.getTime();
            let secondsLeft = Math.round((endTimeToDate - timeNow) / MILLIS_OF_SECONDS);
            if (secondsLeft <= 0) {
                setTimer(() => false);
                setEndTime(() => null);
                secondsLeft = 0;
                const dto = {
                    unitId: unitAssignment?.unitId,
                } as CreateUnitEmergencyRequest;
                if (cancelEmergencyModalOpen) {
                    modalControls.close();
                    setCancelEmergencyModalOpen(false);
                }
                createUnitEmergency(dto);
            }
            setSecondsRemaining(() => secondsLeft);
        };
        updateCountDown();
        const intervalId = setInterval(updateCountDown, INTERVAL_DELAY_VALUE);
        return () => clearInterval(intervalId);
    }, [
        timer,
        endTime,
        unitAssignment,
        cancelEmergencyModalOpen,
        modalControls,
        createUnitEmergency,
        setSecondsRemaining,
        setCancelEmergencyModalOpen,
        setTimer,
        setEndTime,
    ]);

    const toggleEmergencyTimer = () => {
        // If department does not use countdown timer, send the DTO immediately.
        if (!timer && !unitEmergencyActivationCountdownEnabled) {
            const dto = {
                unitId: unitAssignment?.unitId,
            } as CreateUnitEmergencyRequest;
            createUnitEmergency(dto);
            return;
        }
        // Trigger countdown, which will go to useEffect
        if (!timer && unitEmergencyActivationCountdownEnabled) {
            const startTime = new Date();
            const endTime = new Date(
                startTime.getTime() + unitEmergencyActivationCountdownSeconds * MILLIS_OF_SECONDS
            );
            setSecondsRemaining(() => unitEmergencyActivationCountdownSeconds);
            setEndTime(() => endTime);
            setTimer(() => true);
            return;
        }
        setTimer(() => false);
        setEndTime(() => null);
    };

    const handleEmergencyTimer = () => {
        if (activeUnitEmergency && activeUnitEmergency.id > 0) {
            return;
        }
        if (timer) {
            setCancelEmergencyModalOpen(true);
            modalControls.open({
                type: 'CANCEL_UNIT_EMERGENCY_ALERT',
                props: {
                    cancel: toggleEmergencyTimer,
                },
            });
            return;
        }
        toggleEmergencyTimer();
    };

    return {
        toggleEmergencyTimer,
        timer,
        secondsRemaining,
        setCancelEmergencyModalOpen,
        handleEmergencyTimer,
    };
}
